import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroImage from '../assets/r17.webp';
import claimImage from '../assets/r16.png';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const AccountsReceivable = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>AI Accounts Receivable Management | MDeRCM</title>
        <meta 
          name="description" 
          content="AI-powered accounts receivable management for healthcare. Reduce outstanding balances, speed up collections and improve cash flow by 35%." 
        />
        <meta 
          name="keywords" 
          content="accounts receivable management, AR automation, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, medical billing outsourcing services, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, healthcare cost reduction strategies, how to improve revenue cycle management, outsourced revenue cycle management services, revenue cycle management services for hospitals, medical billing and coding outsourcing" 
        />
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/accounts-receivable" />
        <meta property="og:title" content="AI Accounts Receivable Management | Healthcare Revenue Cycle Management" />
        <meta property="og:description" content="Automate AR management with AI. 70% faster collection cycle and 60% reduction in outstanding receivables." />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/accounts-receivable" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mdercm.com/assets/accounts-receivable-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Accounts Receivable Management | Healthcare RCM" />
        <meta name="twitter:description" content="70% faster collections with 60% reduction in outstanding receivables." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* BREADCRUMB NAVIGATION */}
      <nav 
        aria-label="Breadcrumb"
        style={{ 
          padding: '20px 5%', 
          backgroundColor: '#f7f9fb',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <ol style={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '8px',
          fontSize: '14px',
          color: '#666',
          listStyle: 'none',
          margin: 0,
          padding: 0
        }}>
          <li>
            <Link 
              to="/" 
              style={{ 
                color: '#666', 
                textDecoration: 'none',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#F26522'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              Home
            </Link>
          </li>
          <li style={{ color: '#999' }}>/</li>
          <li>
            <Link 
              to="/healthcare/ai" 
              style={{ 
                color: '#666', 
                textDecoration: 'none',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#F26522'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              AI Solutions
            </Link>
          </li>
          <li style={{ color: '#999' }}>/</li>
          <li>
            <span style={{ color: '#F26522', fontWeight: '600' }}>Accounts Receivable Management</span>
          </li>
        </ol>
      </nav>

      {/* HERO SECTION */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '650px', zIndex: 2 }}>
          <motion.h1 style={{ fontSize: 'clamp(24px, 5vw, 42px)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.4 }}>
            Medical Revenue Cycle Management Services - AI Accounts Receivable Automation
          </motion.h1>
          <motion.p style={{ fontSize: 'clamp(14px, 2vw, 18px)', marginBottom: '30px', color: '#dddddd' }}>
            Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> offering AI-driven accounts receivable automation. Our <strong>healthcare revenue cycle management services</strong> handle follow-ups, aging reports, and payment insights automatically with proven <strong>healthcare cost reduction strategies</strong>.
          </motion.p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {[
              ['70%', 'Faster Collection Cycle'],
              ['60%', 'Reduction in Outstanding Receivables'],
              ['65%', 'Fewer Payment Disputes']
            ].map(([value, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{value}</div>
                <div style={{ fontSize: '16px', color: '#ffffff' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* HOW IT WORKS SECTION */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
          <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '30px'
        }}>
          Best Revenue Cycle Management Companies - Turn Outstanding Balances into Revenue
        </h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '18px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, we provide comprehensive <Link to="/healthcare/ai" style={{ color: '#F26522', textDecoration: 'none' }}>revenue cycle management consulting services</Link> that include AI-powered accounts receivable automation and <strong>medical billing outsourcing services</strong>.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px'
        }}>
          <div style={{ flex: '1 1 500px', minWidth: '280px', maxWidth: '600px' }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
              Hospital Revenue Cycle Management - Faster Follow-ups, Better Insights, Stronger Collections
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              With increasing patient balances and payer delays, managing A/R efficiently is critical. <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link>'s AI tracks aging accounts, analyzes payment trends, and prioritizes high-value claims—helping you recover more, faster.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              Our <strong>revenue cycle management services for hospitals</strong> implement proven <strong>healthcare cost reduction strategies</strong> by automating AR workflows. Learn <strong>how to improve revenue cycle management</strong> with intelligent AI that accelerates collections by 70% and reduces outstanding receivables by 60%.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>MDeRCM</Link>'s <strong>outsourced revenue cycle management services</strong> combine <strong>medical billing and coding outsourcing</strong> with advanced AR automation to create a comprehensive solution that maximizes cash flow and minimizes days in A/R.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
            }}
          >
            <img 
              src={claimImage} 
              alt="AI Accounts Receivable Management for Healthcare Revenue Cycle" 
              style={{ width: '100%', height: 'auto', display: 'block' }}
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.section>

      {/* KEY FEATURES - 4x2 GRID */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#f7f9fb' 
      }}>
        <style>{`
          .ar-features-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            max-width: 1400px;
            margin: 0 auto;
          }
          @media (max-width: 1200px) {
            .ar-features-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          @media (max-width: 900px) {
            .ar-features-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 600px) {
            .ar-features-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Revenue Cycle Management Services USA - Key AR Features
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          Our <strong>revenue cycle management services USA</strong> provide intelligent accounts receivable automation designed for <strong>hospital revenue cycle management</strong> excellence.
        </p>

        <div className="ar-features-grid">
          {[
            {
              icon: '📊',
              title: 'Smart Aging Analysis',
              desc: 'AI-powered aging analysis automatically categorizes and prioritizes accounts based on payment probability and value.'
            },
            {
              icon: '🎯',
              title: 'Intelligent Prioritization',
              desc: 'Machine learning algorithms identify high-value claims and optimal follow-up timing for maximum recovery.'
            },
            {
              icon: '📞',
              title: 'Automated Follow-ups',
              desc: 'Automated outreach campaigns via email, phone, and patient portals reduce manual workload by 80%.'
            },
            {
              icon: '💰',
              title: 'Payment Plan Management',
              desc: 'AI manages payment plans, sends reminders, and tracks compliance to improve patient collections.'
            },
            {
              icon: '📈',
              title: 'Predictive Analytics',
              desc: 'Advanced analytics predict payment likelihood and identify at-risk accounts before they age further.'
            },
            {
              icon: '🔍',
              title: 'Root Cause Analysis',
              desc: 'AI identifies patterns in unpaid claims and denials to address systemic issues proactively.'
            },
            {
              icon: '⚡',
              title: 'Real-Time Dashboards',
              desc: 'Live dashboards provide complete visibility into A/R performance, trends, and collection metrics.'
            },
            {
              icon: '🔄',
              title: 'Workflow Automation',
              desc: 'End-to-end automation from identification to resolution streamlines the entire collection process.'
            }
          ].map((feature, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 10px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-8px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{feature.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {feature.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6'
              }}>
                {feature.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS PROCESS - 4 STEPS */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#ffffff' 
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          How to Improve Revenue Cycle Management with AI AR Automation
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          Discover <strong>how to improve revenue cycle management</strong> through our proven 4-step AI-powered AR automation process.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              step: '01',
              title: 'Identify',
              desc: 'AI continuously monitors accounts receivable and identifies aging accounts requiring immediate attention.'
            },
            {
              step: '02',
              title: 'Analyze',
              desc: 'Machine learning analyzes payment patterns, payer behavior, and claim details to prioritize follow-ups.'
            },
            {
              step: '03',
              title: 'Engage',
              desc: 'Automated outreach via multiple channels engages payers and patients with personalized communications.'
            },
            {
              step: '04',
              title: 'Resolve',
              desc: 'AI tracks responses, manages disputes, and facilitates payment resolution while documenting all activities.'
            }
          ].map((step, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '40px 30px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#F26522',
                color: '#ffffff',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: '700',
                boxShadow: '0 4px 15px rgba(242, 101, 34, 0.4)'
              }}>
                {step.step}
              </div>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '15px',
                marginTop: '20px'
              }}>
                {step.title}
              </h3>
              <p style={{ 
                fontSize: '16px', 
                color: '#555', 
                lineHeight: '1.7'
              }}>
                {step.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* BENEFITS SECTION - 4x2 GRID */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <style>{`
          .ar-benefits-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            max-width: 1400px;
            margin: 0 auto;
          }
          @media (max-width: 1200px) {
            .ar-benefits-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          @media (max-width: 900px) {
            .ar-benefits-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 600px) {
            .ar-benefits-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Healthcare Cost Reduction Strategies - Measurable AR Benefits
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          Implement proven <strong>healthcare cost reduction strategies</strong> through AI-powered AR automation and achieve measurable results.
        </p>

        <div className="ar-benefits-grid">
          {[
            {
              icon: '💵',
              title: 'Reduce Days in A/R',
              desc: 'Accelerate collections by 70% and reduce average days in A/R from 45+ to under 30 days.'
            },
            {
              icon: '⚡',
              title: 'Lower Operating Costs',
              desc: 'Cut AR management costs by 60% through automation, eliminating manual follow-up workload.'
            },
            {
              icon: '📈',
              title: 'Increase Cash Flow',
              desc: 'Improve cash flow by 40% through faster collections and reduced outstanding balances.'
            },
            {
              icon: '🎯',
              title: 'Maximize Recovery Rates',
              desc: 'Recover 30% more from aged accounts through intelligent prioritization and timing.'
            },
            {
              icon: '🔍',
              title: 'Better Visibility',
              desc: 'Real-time dashboards provide complete transparency into A/R performance and trends.'
            },
            {
              icon: '✅',
              title: 'Improve Patient Satisfaction',
              desc: 'Automated, personalized communication improves patient experience and payment compliance.'
            },
            {
              icon: '⏰',
              title: 'Save Staff Time',
              desc: 'Free staff from manual follow-ups to focus on complex cases and patient care.'
            },
            {
              icon: '📊',
              title: 'Data-Driven Insights',
              desc: 'AI analytics identify root causes of delayed payments and opportunities for improvement.'
            }
          ].map((benefit, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '35px 25px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '12px' 
              }}>
                {benefit.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {benefit.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* RELATED SERVICES SECTION */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#f7f9fb' 
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#001F2B'
          }}
        >
          Medical Billing Outsourcing Services - Complete RCM Solutions
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Explore our comprehensive <strong>medical billing outsourcing services</strong> designed to optimize every aspect of your revenue cycle.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              title: 'Payment Posting',
              desc: 'AI-powered payment posting automation processes remittances 50x faster with 99.9% accuracy.',
              link: '/healthcare/ai/payment-posting',
              icon: '💳'
            },
            {
              title: 'Denial Management',
              desc: 'Intelligent denial prevention and appeals management to maximize revenue recovery.',
              link: '/healthcare/ai/denial-management',
              icon: '🔄'
            },
            {
              title: 'Eligibility Verification',
              desc: 'Real-time insurance eligibility checks reduce claim denials and improve first-pass rates.',
              link: '/healthcare/ai/eligibility-check',
              icon: '✓'
            },
            {
              title: 'Prior Authorization',
              desc: 'Automated prior authorization processing accelerates approvals and reduces delays.',
              link: '/healthcare/ai/prior-authorization',
              icon: '📋'
            },
            {
              title: 'Patient Intake',
              desc: 'Streamlined patient registration and demographic verification for accurate billing.',
              link: '/healthcare/ai/patient-intake',
              icon: '👤'
            },
            {
              title: 'Contract Repricing',
              desc: 'Automated insurance contract analysis ensures accurate reimbursement rates.',
              link: '/healthcare/ai/InsuranceContract-Repricing',
              icon: '📊'
            },
            {
              title: 'Compliance Agent',
              desc: 'AI-powered compliance monitoring ensures adherence to regulatory requirements.',
              link: '/healthcare/ai/compliance-agent',
              icon: '🔒'
            },
            {
              title: 'Policy Verification',
              desc: 'Comprehensive policy status verification prevents coverage-related denials.',
              link: '/healthcare/ai/policy-status-verification',
              icon: '📄'
            }
          ].map((service, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              whileHover={{ 
                boxShadow: '0 10px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-8px)'
              }}
            >
              <div style={{ 
                fontSize: '40px', 
                marginBottom: '15px',
                color: '#F26522'
              }}>
                {service.icon}
              </div>
              <h3 style={{ 
                fontSize: '22px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '15px' 
              }}>
                {service.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6',
                marginBottom: '20px',
                flex: 1
              }}>
                {service.desc}
              </p>
              <Link 
                to={service.link}
                style={{ 
                  color: '#F26522', 
                  textDecoration: 'none', 
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'gap 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.gap = '12px'}
                onMouseOut={(e) => e.currentTarget.style.gap = '8px'}
              >
                Learn More →
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE MDERCM */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading <strong>revenue cycle management services USA</strong> with proven results and dedicated support.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '🏆',
              title: 'Industry-Leading Expertise',
              desc: 'Over 10+ years of experience in healthcare revenue cycle management with proven track record of success.'
            },
            {
              icon: '🤖',
              title: 'Advanced AI Technology',
              desc: 'Cutting-edge AI and machine learning algorithms that continuously improve accuracy and efficiency.'
            },
            {
              icon: '💵',
              title: 'Maximize Revenue',
              desc: 'Increase revenue by up to 30% through optimized AR management, reduced days in A/R, and faster collections.'
            },
            {
              icon: '⚡',
              title: '24/7 Support',
              desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick issue resolution.'
            },
            {
              icon: '🔒',
              title: 'HIPAA Compliant',
              desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy.'
            },
            {
              icon: '📈',
              title: 'Scalable Solutions',
              desc: 'Flexible services that grow with your practice, from small clinics to large hospital systems.'
            },
            {
              icon: '💡',
              title: 'Transparent Reporting',
              desc: 'Real-time dashboards and detailed analytics providing complete visibility into your revenue cycle.'
            },
            {
              icon: '🎯',
              title: 'Proven ROI',
              desc: 'Measurable results with most clients seeing positive ROI within 3-6 months of implementation.'
            },
          ].map((reason, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '35px 25px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '12px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.article>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '60px'
        }}>
          <Link 
            to="/free-trial"
            style={{
              backgroundColor: '#F26522',
              color: '#ffffff',
              padding: '18px 50px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '18px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d94d12';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#F26522';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Start Your Free Trial Today
          </Link>
        </div>
      </section>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default AccountsReceivable;